import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { terms } from '../../language/terms';
import { customElement, query, state } from 'lit/decorators.js';
import styles from './login-styles.css?inline'; // updated import
import { styles as sharedStyles } from '../../styles/shared-styles';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/button/button.js'; // import Shoelace button
import '@shoelace-style/shoelace/dist/components/icon/icon.js'; // import Shoelace icon
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { resolveRouterPath, router } from '../../router'; // updated import

import { write } from '../../language/lang';
import { ApiFactory } from '../../api/agwasuite-api-factory';

import {
  mergeProfileWithCredentials,
  UserProfile,
} from '../../datatypes/user-profile';
import { doEventWithOverlay } from '../../utility/ui-overlay';
import { noParalellExecutionAsync } from '../../utility/callbacks';
import { LoginFailureReason } from '../../api/agwa-api-model';
import { isSame } from '../../utility/strings/strings-utils';
import { ApiLogin } from '../../api/core/agwasuite-api-login';
import { sessionManager } from '../../services/session-manager';
import type { RememberedUser } from '../../datatypes/session-types';

// Define an enum for different view states
enum LoginViewState {
  AccountPicker, // New: Show remembered accounts
  SignIn,
  ForgotPassword,
  PasswordResetSent,
  TwoFactor,
  PasswordChange,
}

interface LoginState {
  username: string;
  password: string;
  twoFactorCode?: string;
  passwordChangeToken?: string;
  newPassword?: string;
  loading: boolean;
  viewState: LoginViewState;
  /** Preserve the original password for password change flow so we can submit it with the change token */
  originalPassword?: string;
  /** If true, session cookie has expiry. If false, cookie expires when browser closes. */
  staySignedIn: boolean;
}

interface ErrorState {
  message?: string;
  failureReason: LoginFailureReason;
}

/**
 * What the sign-in alert says for a failure (#398 C15 review 1 F3, the SA's ruling; D-165).
 *
 * The reason is the contract and the page owns the words for every reason it names here. For any
 * other reason the server's text is the only account there is (a password rule's reason, a
 * cool-off's time left), so it is shown, falling back to a generic sentence when there is none.
 * Applied where the server's answer is stored, so a message the page sets itself ("Passwords do
 * not match") is drawn as it is whatever reason came before it.
 *
 * Since D-165 the server answers every failure that turns on the account or its password (no such
 * user, a wrong password, more than one match, locked, deleted, a blocked company, no password
 * sign-in) as InvalidCredentials, so that is the one sentence a failed sign-in shows and nothing
 * tells an unknown username from a real one. The reasons it no longer sends (UserNotFound,
 * AccountLocked, AccountDeleted, MulitpleMatchingUsers) stay in the contract and have no words
 * here. A password-change token that ran out (PasswordChangeTokenInvalid) arrives only after the
 * password is proved, so it keeps its own sentence (@SIGNIN-005).
 */
export function loginErrorText(
  reason: LoginFailureReason,
  serverMessage: string | undefined
): string {
  switch (true) {
    case isSame(reason, 'InvalidCredentials'):
      return write`Invalid username or password.`;
    case isSame(reason, 'PasswordChangeTokenInvalid'):
      return write`Your password change took too long. Please sign in again.`;
    default:
      return serverMessage || write`An error occurred. Please try again.`;
  }
}

export type SetUserProfileBinder = (
  profile: UserProfile | undefined
) => Promise<void>;
let updateUserProfile: SetUserProfileBinder | undefined = undefined;

export function setUpdateUserProfileBinder(
  setUserProfile: SetUserProfileBinder
) {
  updateUserProfile = setUserProfile;
}
@customElement('app-login-internal')
export class AppLoginInternal extends WithUserProfileConsumer(LitElement) {
  static styles = [sharedStyles, unsafeCSS(styles)];

  /** Unsubscribe function for session manager events */
  private _sessionUnsubscribe?: () => void;

  @state()
  private loginState: LoginState = {
    username: '',
    password: '',
    loading: false,
    viewState: LoginViewState.SignIn,
    staySignedIn: true,
  };

  @state()
  private errorState: ErrorState = {
    failureReason: 'None',
  };

  @state()
  private rememberedUsers: RememberedUser[] = [];

  /**
   * False until the remembered-users / sessions load has ANSWERED (success or
   * failure). The first interactive view must be the final one: without this
   * gate the SignIn form paints immediately and then swaps to the account
   * picker when the async load lands - yanking the username field out from
   * under a person already typing into it (and out from under the e2e
   * helpers, which is how @CO-BLOCK-002 failed intermittently on 2026-09-06).
   */
  @state() private accountsResolved = false;

  @state()
  private selectedAccount: RememberedUser | null = null;

  /** Active sessions loaded from server - used to check if user already has valid session */
  private activeSessions: import('../../datatypes/session-types').SessionInfo[] =
    [];

  @query('#username-input')
  private usernameInput!: HTMLInputElement;

  @query('#password-input')
  private passwordInput!: HTMLInputElement;

  @query('#twofa-input')
  private twofaInput!: HTMLInputElement;

  @query('#new-password-input')
  private newPasswordInput!: HTMLInputElement;

  @query('#confirm-password-input')
  private confirmPasswordInput!: HTMLInputElement;

  @query('#forgot-username-input')
  private forgotUsernameInput!: HTMLInputElement;

  @query('#forgot-email-input')
  private forgotEmailInput!: HTMLInputElement;

  connectedCallback() {
    super.connectedCallback();

    // Subscribe to remembered users changes (e.g., if another component modifies the list)
    this._sessionUnsubscribe = sessionManager.subscribe((event) => {
      if (event.type === 'remembered-users-changed') {
        // Only reload user list, don't reset view state
        void this.reloadRememberedUsersList();
      }
    });

    // Check if we're adding an account (don't redirect even if logged in)
    const urlParams = new URLSearchParams(window.location.search);
    const isAddingAccount = urlParams.get('addAccount') === 'true';

    if (this.userProfile && !isAddingAccount) {
      void router!.navigate(resolveRouterPath('/')); // updated navigation
    } else {
      // Load remembered users and show account picker if any exist
      void this.loadRememberedUsers();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._sessionUnsubscribe?.();
  }

  /**
   * Reload just the remembered users list without changing view state.
   * Used when responding to external changes.
   */
  private async reloadRememberedUsersList(): Promise<void> {
    try {
      this.rememberedUsers = await sessionManager.getRememberedUsers();
      // If we were in account picker and all users are now gone, switch to sign in
      if (
        this.rememberedUsers.length === 0 &&
        this.loginState.viewState === LoginViewState.AccountPicker
      ) {
        this.switchToNewAccount();
      }
    } catch (err) {
      console.error('Failed to reload remembered users:', err);
    }
  }

  private async loadRememberedUsers(): Promise<void> {
    try {
      // Load both remembered users and active sessions
      const [rememberedUsers, sessionsResult] = await Promise.all([
        sessionManager.getRememberedUsers(),
        sessionManager.loadSessions(),
      ]);

      this.rememberedUsers = rememberedUsers;
      this.activeSessions = sessionsResult.sessions;

      // Check if a specific username was requested (e.g., switching to a remembered user)
      const urlParams = new URLSearchParams(window.location.search);
      const requestedUsername = urlParams.get('username');

      if (requestedUsername) {
        // Find the remembered user matching the username
        const matchingUser = this.rememberedUsers.find(
          (u) => u.username.toLowerCase() === requestedUsername.toLowerCase()
        );
        if (matchingUser) {
          // Pre-select this user - AccountPicker view with selectedAccount shows password entry
          this.selectedAccount = matchingUser;
          this.loginState = {
            ...this.loginState,
            username: matchingUser.username,
            viewState: LoginViewState.AccountPicker,
          };
          return;
        } else {
          // Username not in remembered users - go to sign-in with username pre-filled
          this.loginState = {
            ...this.loginState,
            username: requestedUsername,
            viewState: LoginViewState.SignIn,
          };
          return;
        }
      }

      // If we have remembered users, show the account picker
      if (this.rememberedUsers.length > 0) {
        this.loginState = {
          ...this.loginState,
          viewState: LoginViewState.AccountPicker,
        };
      }
    } catch (err) {
      // Failure falls back to the plain SignIn form - the picker is a
      // convenience, never a gate on logging in at all.
      console.error('Failed to load remembered users:', err);
    } finally {
      this.accountsResolved = true;
    }
  }

  private async performLogin(state: LoginState) {
    const api = ApiFactory.anonymous(ApiLogin);
    this.loginState = { ...state, loading: true };

    try {
      const result = await doEventWithOverlay(
        api.login({
          username: state.username,
          password: state.password,
          twoFactorCode: state.twoFactorCode,
          passwordChangeToken: state.passwordChangeToken,
          newPassword: state.newPassword,
          staySignedIn: state.staySignedIn,
        })
      );

      if (!result.success || !result.data) {
        this.errorState = {
          message: result.error || 'Login failed',
          failureReason: this.errorState.failureReason,
        };
        this.loginState = {
          ...this.loginState,
          loading: false,
        };
        return;
      }

      const loginResult = result.data;

      if (loginResult.success && loginResult.token) {
        // Generate the session ID (same algorithm as server)
        const newSessionId = sessionManager.createAccountKey(
          loginResult.loginCredentials!.loginUserId
        );

        // Persist session ID and user ID to sessionStorage (per-tab isolation)
        // Also update localStorage for new tabs to inherit this session
        sessionStorage.setItem('agwa-active-account', newSessionId);
        sessionStorage.setItem(
          'agwa-active-userid',
          loginResult.loginCredentials!.loginUserId
        );
        localStorage.setItem('agwa-last-account', newSessionId);

        ApiFactory.setAuthToken(
          undefined,
          loginResult.loginCredentials!.loginUserId
        );
        loginResult.token = '';
        loginResult.loginCredentials!.token = loginResult.token;
        // Profile images are fetched via UserDirectory - no need to get them here
        const userProfile = new UserProfile(
          mergeProfileWithCredentials(
            {
              authProvider: 'internal',
            },
            loginResult.loginCredentials!
          )
        );

        // Remember this user for future logins (including profile image URL from login result)
        await sessionManager.rememberUser({
          userId: loginResult.loginCredentials!.loginUserId,
          sessionId: sessionManager.createAccountKey(
            loginResult.loginCredentials!.loginUserId
          ),
          username: loginResult.loginCredentials!.username,
          displayName: loginResult.loginCredentials!.displayName,
          emailAddress: loginResult.loginCredentials!.emailAddress ?? undefined,
          profileImageUrl:
            loginResult.loginCredentials!.profileImageUrl ?? undefined,
        });

        // Reload sessions to sync the session manager's currentSessionId with the server
        await sessionManager.loadSessions();

        await updateUserProfile?.(userProfile);
        void router!.navigate(resolveRouterPath(''));
        return;
      }

      // The server's answer is drawn in the page's words for the reasons the page names
      // (loginErrorText), and in its own words only for the rest.
      this.errorState = {
        message: loginErrorText(
          loginResult.errorReason,
          loginResult.errorMessage
        ),
        failureReason: loginResult.errorReason,
      };

      // Set the appropriate view state based on the error reason
      let newViewState = this.loginState.viewState;
      if (
        isSame(
          this.errorState.failureReason,
          'TwoFactorRequired',
          'TwoFactorInvalid'
        )
      ) {
        newViewState = LoginViewState.TwoFactor;
      } else if (
        isSame(
          this.errorState.failureReason,
          'PasswordChangeRequired',
          'PasswordExpired',
          'PasswordNotGoodEnough'
        )
      ) {
        newViewState = LoginViewState.PasswordChange;
      }

      this.loginState = {
        ...this.loginState,
        loading: false,
        passwordChangeToken: loginResult.passwordChangeToken,
        // preserve original password only once (first transition)
        originalPassword:
          this.loginState.originalPassword ?? this.loginState.password,
        viewState: newViewState,
      };

      if (
        isSame(
          this.errorState.failureReason,
          'InvalidCredentials',
          'UserNotFound',
          'AccountLocked',
          'AccountDeleted',
          'PasswordChangeTokenInvalid',
          'UnknownError'
        )
      ) {
        this.resetToInitialState();
        this.errorState = {
          ...this.errorState,
          message: loginErrorText(
            loginResult.errorReason,
            loginResult.errorMessage
          ),
        };
      }
      // Fallback: if a passwordChangeToken exists but we did not switch views (due to unexpected casing/value), force password change view
      if (
        loginResult.passwordChangeToken &&
        this.loginState.viewState === LoginViewState.SignIn &&
        isSame(
          this.errorState.failureReason,
          'Passwordchangerequired',
          'PasswordChangeRequired'
        )
      ) {
        this.loginState = {
          ...this.loginState,
          viewState: LoginViewState.PasswordChange,
          passwordChangeToken: loginResult.passwordChangeToken,
          originalPassword:
            this.loginState.originalPassword ?? this.loginState.password,
        };
      }
    } catch (error) {
      this.errorState = {
        message: 'An unexpected error occurred',
        failureReason: 'None',
      };
      this.loginState = { ...this.loginState, loading: false };
    }
  }

  private actionSignIn = noParalellExecutionAsync(async () => {
    const state = {
      ...this.loginState,
      username: this.usernameInput.value,
      password: this.passwordInput.value,
    };
    await this.performLogin(state);
  });

  private actionVerify2FA = noParalellExecutionAsync(async () => {
    const state = {
      ...this.loginState,
      twoFactorCode: this.twofaInput.value,
    };
    await this.performLogin(state);
  });

  private actionChangePassword = noParalellExecutionAsync(async () => {
    if (this.newPasswordInput.value !== this.confirmPasswordInput.value) {
      this.errorState = {
        message: 'Passwords do not match',
        failureReason: this.errorState.failureReason,
      };
      return;
    }

    const state = {
      ...this.loginState,
      // ensure original password is supplied for backend validation
      password:
        this.loginState.originalPassword ?? this.loginState.password ?? '',
      newPassword: this.newPasswordInput.value,
    };
    await this.performLogin(state);
  });

  private switchToForgotPassword() {
    this.loginState = {
      ...this.loginState,
      viewState: LoginViewState.ForgotPassword,
    };
    this.errorState = {
      message: undefined,
      failureReason: 'None',
    };
  }

  private backToLogin() {
    this.loginState = {
      ...this.loginState,
      viewState: LoginViewState.SignIn,
    };
    this.errorState = {
      message: undefined,
      failureReason: 'None',
    };
  }

  private handleUsernameInput() {
    // Clear the email input if username is entered
    if (
      this.forgotUsernameInput &&
      this.forgotUsernameInput.value &&
      this.forgotEmailInput
    ) {
      this.forgotEmailInput.value = '';
    }
  }

  private handleEmailInput() {
    // Clear the username input if email is entered
    if (
      this.forgotEmailInput &&
      this.forgotEmailInput.value &&
      this.forgotUsernameInput
    ) {
      this.forgotUsernameInput.value = '';
    }
  }

  private requestPasswordReset = noParalellExecutionAsync(async () => {
    // Check if either username or email is provided, but not both
    const hasUsername = this.forgotUsernameInput?.value?.trim() !== '';
    const hasEmail = this.forgotEmailInput?.value?.trim() !== '';

    if (!hasUsername && !hasEmail) {
      this.errorState = {
        message: 'Please enter either a username or an email address',
        failureReason: 'None',
      };
      return;
    }

    // Extract the value to use (either username or email)
    const username = hasUsername
      ? this.forgotUsernameInput.value.trim()
      : undefined;
    const emailAddress = hasEmail
      ? this.forgotEmailInput.value.trim()
      : undefined;
    const inputValue = username || emailAddress || '';

    this.loginState = { ...this.loginState, loading: true };

    try {
      const api = ApiFactory.anonymous(ApiLogin);

      const result = await doEventWithOverlay(
        api.requestPasswordReset({
          username: username,
          emailAddress: emailAddress,
        })
      );

      this.loginState = {
        ...this.loginState,
        loading: false,
      };

      if (result.success) {
        // Show success message
        this.loginState = {
          ...this.loginState,
          viewState: LoginViewState.PasswordResetSent,
          username: inputValue, // Store for reference
        };
      } else {
        this.errorState = {
          message: result.error || 'Failed to process your request',
          failureReason: 'None',
        };
      }
    } catch (error) {
      this.loginState = { ...this.loginState, loading: false };
      this.errorState = {
        message: 'An unexpected error occurred',
        failureReason: 'None',
      };
    }
  });
  private resetToInitialState() {
    this.loginState = {
      username: '',
      password: '',
      loading: false,
      viewState: LoginViewState.SignIn,
      staySignedIn: true,
    };
    this.errorState = {
      message: undefined,
      failureReason: 'None',
    };
  }

  private getErrorDisplay() {
    if (
      !this.errorState.message &&
      isSame(this.errorState.failureReason, 'None')
    ) {
      return '';
    }

    return html`
      <sl-alert variant="danger" open>
        <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
        <strong
          >${
            this.errorState.message ||
            loginErrorText(this.errorState.failureReason, undefined)
          }</strong
        >
        <sl-button
          slot="action"
          size="small"
          variant="text"
          @click=${() => {
            this.errorState = {
              message: undefined,
              failureReason: 'None',
            };
          }}
        >
          Dismiss
        </sl-button>
      </sl-alert>
    `;
  }

  private getTemplateForState() {
    if (this.loginState.loading) {
      return this.getLoadingTemplate();
    }

    return html`${this.getStateTemplate()}`;
  }

  private getStateTemplate() {
    switch (this.loginState.viewState) {
      case LoginViewState.AccountPicker:
        return this.getAccountPickerTemplate();
      case LoginViewState.ForgotPassword:
        return this.getForgotPasswordTemplate();
      case LoginViewState.PasswordResetSent:
        return this.getPasswordResetSentTemplate();
      case LoginViewState.TwoFactor:
      case LoginViewState.PasswordChange:
        // Handle based on failure reason for backward compatibility
        if (
          isSame(
            this.errorState.failureReason,
            'TwoFactorRequired',
            'TwoFactorInvalid'
          )
        ) {
          return this.getTwoFactorTemplate();
        } else if (
          isSame(
            this.errorState.failureReason,
            'PasswordChangeRequired',
            'PasswordExpired',
            'PasswordNotGoodEnough'
          )
        ) {
          return this.getPasswordChangeTemplate();
        }
        // Fallback to sign in if no specific failure reason
        return this.getSignInTemplate();
      default:
        return this.getSignInTemplate();
    }
  }

  private getLoadingTemplate() {
    return html`
      <div class="login-form">
        <!--     <sl-spinner></sl-spinner>
      <p>Please wait...</p>    -->
      </div>
    `;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Account Picker Template
  // ─────────────────────────────────────────────────────────────────────────────

  private getAccountPickerTemplate() {
    // If a user is selected, show password entry
    if (this.selectedAccount) {
      return this.getPasswordEntryTemplate();
    }

    return html`
      <div class="account-picker">
        <h2>Choose an account</h2>
        ${this.getErrorDisplay()}
        <div class="account-list">
          ${this.rememberedUsers.map((user) => this.renderAccountItem(user))}
        </div>
        <div
          class="account-add"
          @click=${() => this.switchToNewAccount()}
          @keydown=${(e: KeyboardEvent) =>
            e.key === 'Enter' && this.switchToNewAccount()}
          tabindex="0"
          role="button"
        >
          <sl-icon name="plus-circle"></sl-icon>
          <span>Use another account</span>
        </div>
      </div>
    `;
  }

  private renderAccountItem(user: RememberedUser) {
    // Use stored profileImageUrl from RememberedUser (available even when not logged in)
    const profileImageUrl = user.profileImageUrl;
    return html`
      <div
        class="account-item"
        @click=${() => this.selectAccount(user)}
        @keydown=${(e: KeyboardEvent) =>
          e.key === 'Enter' && this.selectAccount(user)}
        tabindex="0"
        role="button"
      >
        <div class="account-avatar">
          ${
            profileImageUrl
              ? html`<img src="${profileImageUrl}" alt="${user.displayName}" />`
              : html`<sl-icon name="person-circle"></sl-icon>`
          }
        </div>
        <div class="account-info">
          <div class="account-name">${user.displayName}</div>
          <div class="account-email">${user.username}</div>
        </div>
        <div
          class="account-forget"
          @click=${(e: Event) => this.forgetAccount(e, user)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              void this.forgetAccount(e, user);
            }
          }}
          tabindex="0"
          role="button"
          title="Forget this account"
        >
          <sl-icon name="x-circle"></sl-icon>
        </div>
      </div>
    `;
  }

  private getPasswordEntryTemplate() {
    const user = this.selectedAccount!;
    // Use stored profileImageUrl from RememberedUser
    const profileImageUrl = user.profileImageUrl;
    return html`
      <div class="account-picker">
        ${this.getErrorDisplay()}
        <div class="password-entry">
          <div class="password-entry-header">
            <div class="account-avatar">
              ${
                profileImageUrl
                  ? html`<img
                      src="${profileImageUrl}"
                      alt="${user.displayName}"
                    />`
                  : html`<sl-icon name="person-circle"></sl-icon>`
              }
            </div>
            <div class="password-entry-info">
              <div class="account-name">${user.displayName}</div>
              <div class="account-email">${user.username}</div>
            </div>
            <div
              class="password-entry-back"
              @click=${() => this.clearSelectedAccount()}
              title="Choose different account"
            >
              <sl-icon name="arrow-left"></sl-icon>
            </div>
          </div>

          <form
            @submit=${(e: Event) => {
              e.preventDefault();
              void this.actionSignInSelectedAccount();
            }}
          >
            <div class="form-field">
              <label for="password-input">Password</label>
              <sl-input
                id="password-input"
                name="password"
                placeholder="Enter your password"
                type="password"
                autocomplete="current-password"
              ></sl-input>
            </div>

            <div class="form-field stay-signed-in">
              <sl-checkbox
                ?checked=${this.loginState.staySignedIn}
                @sl-change=${(e: Event) => {
                  const checkbox = e.target as HTMLInputElement;
                  this.loginState = {
                    ...this.loginState,
                    staySignedIn: checkbox.checked,
                  };
                }}
              >
                Stay signed in
              </sl-checkbox>
            </div>

            <sl-button variant="primary" type="submit"> Sign In </sl-button>
          </form>
        </div>

        <div
          class="account-add"
          @click=${() => this.switchToNewAccount()}
          tabindex="0"
          role="button"
        >
          <sl-icon name="plus-circle"></sl-icon>
          <span>Use a different account</span>
        </div>
      </div>
    `;
  }

  private async selectAccount(user: RememberedUser): Promise<void> {
    // Check if this user already has an active session
    const existingSession = this.activeSessions.find(
      (s) => s.sessionId === user.sessionId
    );

    if (existingSession) {
      // User has an active session - try to switch to it
      this.loginState = { ...this.loginState, loading: true };
      this.errorState = { message: undefined, failureReason: 'None' };

      const switchResult = await sessionManager.switchSession(user.sessionId);
      // Switch failed (session may have expired) - fall through to password entry
      this.loginState = { ...this.loginState, loading: false };
      if (switchResult.success) {
        // Successfully switched - reload page to reinitialize app state
        // Using reload because the auth context has changed and all caches/state
        // need to be cleared. The new session's isLoggedIn() will return true.
        void router!.navigate(resolveRouterPath(''));
        return;
      }

      // Refresh sessions since this one is invalid
      this.activeSessions = this.activeSessions.filter(
        (s) => s.sessionId !== user.sessionId
      );
    }

    // No active session or switch failed - ask for password
    this.selectedAccount = user;
    this.loginState = {
      ...this.loginState,
      username: user.username,
    };
    this.errorState = { message: undefined, failureReason: 'None' };
    // Focus the password input after render
    void this.updateComplete.then(() => {
      this.passwordInput?.focus();
    });
  }

  private clearSelectedAccount(): void {
    this.selectedAccount = null;
    this.loginState = {
      ...this.loginState,
      password: '',
    };
    this.errorState = { message: undefined, failureReason: 'None' };
  }

  private switchToNewAccount(): void {
    this.selectedAccount = null;
    this.loginState = {
      ...this.loginState,
      username: '',
      password: '',
      viewState: LoginViewState.SignIn,
    };
    this.errorState = { message: undefined, failureReason: 'None' };
  }

  private async forgetAccount(e: Event, user: RememberedUser): Promise<void> {
    e.stopPropagation();
    await sessionManager.forgetUser(user.userId);
    this.rememberedUsers = this.rememberedUsers.filter(
      (u) => u.userId !== user.userId
    );
    // If no more remembered users, switch to sign in
    if (this.rememberedUsers.length === 0) {
      this.switchToNewAccount();
    }
  }

  private actionSignInSelectedAccount = noParalellExecutionAsync(async () => {
    if (!this.selectedAccount) return;

    const state: LoginState = {
      ...this.loginState,
      username: this.selectedAccount.username,
      password: this.passwordInput?.value ?? '',
      viewState: LoginViewState.AccountPicker,
    };

    await this.performLogin(state);
  });

  private getSignInTemplate() {
    return html`
      <form
        class="login-form"
        id="login-form"
        @submit=${(e: Event) => {
          e.preventDefault();
          void this.actionSignIn();
        }}
      >
        ${this.getErrorDisplay()}
        <div class="form-field">
          <label for="username-input">Username</label>
          <sl-input
            id="username-input"
            name="username"
            placeholder="Username"
            type="text"
            autocomplete="username"
          ></sl-input>
        </div>

        <div class="form-field">
          <label for="password-input">Password</label>
          <sl-input
            id="password-input"
            name="password"
            placeholder="Password"
            type="password"
            autocomplete="current-password"
          ></sl-input>
        </div>

        <div class="form-field stay-signed-in">
          <sl-checkbox
            ?checked=${this.loginState.staySignedIn}
            @sl-change=${(e: Event) => {
              const checkbox = e.target as HTMLInputElement;
              this.loginState = {
                ...this.loginState,
                staySignedIn: checkbox.checked,
              };
            }}
          >
            Stay signed in
          </sl-checkbox>
        </div>

        <sl-button variant="primary" type="submit" @click=${this.actionSignIn}
          >Sign In</sl-button
        >

        <div class="forgot-links">
          ${
            this.rememberedUsers.length > 0
              ? html`
                  <a
                    href="#"
                    @click=${(e: Event) => {
                      e.preventDefault();
                      this.backToAccountPicker();
                    }}
                    >Back to accounts</a
                  >
                  <span>|</span>
                `
              : nothing
          }
          <a
            href="#"
            @click=${(e: Event) => {
              e.preventDefault();
              this.switchToForgotPassword();
            }}
            >Forgot password or username?</a
          >
        </div>
      </form>
    `;
  }

  private backToAccountPicker(): void {
    this.loginState = {
      ...this.loginState,
      viewState: LoginViewState.AccountPicker,
    };
    this.errorState = { message: undefined, failureReason: 'None' };
  }

  private getForgotPasswordTemplate() {
    return html`
      <form
        class="login-form"
        id="forgot-password-form"
        @submit=${(e: Event) => {
          e.preventDefault();
          void this.requestPasswordReset();
        }}
      >
        ${this.getErrorDisplay()}
        <h2>Forgot Password or Username</h2>
        <p>
          Enter either your username or email address to receive assistance.
        </p>

        <div class="forgot-option-container">
          <div class="form-field">
            <label for="forgot-username-input">Username</label>
            <sl-input
              id="forgot-username-input"
              name="username"
              placeholder="Enter your username"
              type="text"
              autocomplete="username"
              @input=${this.handleUsernameInput}
            ></sl-input>
          </div>

          <div class="form-field">
            <label for="forgot-email-input">Email Address</label>
            <sl-input
              id="forgot-email-input"
              name="email"
              placeholder="Enter your email address"
              type="email"
              autocomplete="email"
              @input=${this.handleEmailInput}
            ></sl-input>
          </div>
        </div>

        <div class="form-help-text">
          <p>Enter either your username OR email address (not both).</p>
        </div>

        <div class="button-group">
          <sl-button
            variant="primary"
            type="submit"
            @click=${this.requestPasswordReset}
          >
            Submit
          </sl-button>
          <sl-button variant="neutral" @click=${this.backToLogin}>
            Back to Login
          </sl-button>
        </div>
      </form>
    `;
  }

  private getPasswordResetSentTemplate() {
    return html`
      <div class="login-form">
        ${this.getErrorDisplay()}
        <sl-alert variant="success" open>
          <sl-icon slot="icon" name="check-circle"></sl-icon>
          <strong>Request Sent!</strong>
          <p>
            If an account exists for the information you provided, you will
            receive an email with further instructions.
          </p>
        </sl-alert>

        <div class="button-group" style="margin-top: 20px;">
          <sl-button variant="primary" @click=${this.backToLogin}>
            Back to Login
          </sl-button>
        </div>
      </div>
    `;
  }

  private getTwoFactorTemplate() {
    return html`
      <form
        class="login-form"
        id="twofa-form"
        @submit=${(e: Event) => {
          e.preventDefault();
          void this.actionVerify2FA();
        }}
      >
        ${this.getErrorDisplay()}
        <div class="form-field">
          <label>Username</label>
          <sl-input
            value=${this.loginState.username}
            disabled
            readonly
          ></sl-input>
        </div>
        <div class="form-field">
          <label for="twofa-input">Two-Factor Code</label>
          <sl-input
            id="twofa-input"
            name="otp"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="one-time-code"
            placeholder="Enter code"
          ></sl-input>
        </div>
        <div class="button-group">
          <sl-button
            variant="primary"
            type="submit"
            @click=${this.actionVerify2FA}
          >
            Verify
          </sl-button>
          <sl-button variant="neutral" @click=${this.resetToInitialState}>
            Start Again
          </sl-button>
        </div>
      </form>
    `;
  }

  private getPasswordChangeTemplate() {
    return html`
      <form
        class="login-form"
        id="password-change-form"
        @submit=${(e: Event) => {
          e.preventDefault();
          void this.actionChangePassword();
        }}
      >
        ${this.getErrorDisplay()}
        <div class="form-field">
          <label>Username</label>
          <sl-input
            value=${this.loginState.username}
            disabled
            readonly
          ></sl-input>
        </div>
        <div class="form-field">
          <label for="new-password-input">New Password</label>
          <sl-input
            id="new-password-input"
            name="new-password"
            type="password"
            autocomplete="new-password"
            placeholder="Enter new password"
          ></sl-input>
        </div>
        <div class="form-field">
          <label for="confirm-password-input">Confirm Password</label>
          <sl-input
            id="confirm-password-input"
            name="confirm-password"
            type="password"
            autocomplete="new-password"
            placeholder="Confirm new password"
          ></sl-input>
        </div>
        <div class="button-group">
          <sl-button
            variant="primary"
            type="submit"
            @click=${this.actionChangePassword}
          >
            Change Password
          </sl-button>
          <sl-button variant="neutral" @click=${this.resetToInitialState}>
            Start Again
          </sl-button>
        </div>
      </form>
    `;
  }

  render() {
    return html`
      <div class="header">
        <h1>
          This area is restricted to ${terms.agwa.value}
          ${terms.member.lowerPlural} only.
        </h1>
      </div>

      <img src="/assets/images/banner.png" alt="AGWA Logo" class="logo" />

      ${
        this.accountsResolved
          ? this.getTemplateForState()
          : html`<sl-spinner class="view-resolving"></sl-spinner>`
      }
    `;
  }
}
